import {useState} from 'react';

/**
 * The draft / parse / submit logic of the "Добавить событие" flow, shared by the v0.7
 * SubmitEventModal and the v0.8 AddEventModal. It talks to /api/events/parse-url/ and
 * /api/events/submit/ with the payloads v0.7 has always sent; the visuals (steps, map
 * picking, toast) stay with each modal.
 */

const URL_RE = /^https?:\/\//i;
const JSON_HEADERS = {'Content-Type': 'application/json'};
const DEFAULT_DURATION_MS = 2 * 60 * 60 * 1000;

const pad = (value) => String(value).padStart(2, '0');

const formatLocalDateTime = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const localDateTimeToISO = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toISOString();
};

/** A fresh draft: no URL, no point, start now and end two hours later (local datetime-local values). */
const initialDraft = () => {
    const now = new Date();
    return {
        url: null,
        name: '',
        title: '',
        description: '',
        imageUrl: null,
        latitude: null,
        longitude: null,
        startAt: formatLocalDateTime(now),
        endAt: formatLocalDateTime(new Date(now.getTime() + DEFAULT_DURATION_MS)),
    };
};

const postJson = (path, body) => fetch(path, {method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body)});

/** Returns the label of the first problem of the raw URL input, or null when it can be parsed. */
const urlProblem = (input, t) => {
    if (!input) return t.submitErrUrlRequired;
    if (!URL_RE.test(input)) return t.submitErrUrlScheme;
    return null;
};

/** Returns {error} for an incomplete draft, else {payload} — the /api/events/submit/ body. */
const submitPayload = (draft, t) => {
    if (draft.latitude == null || draft.longitude == null) return {error: t.submitErrNoPlace};
    if (!draft.name.trim()) return {error: t.submitErrNoName};
    const startIso = localDateTimeToISO(draft.startAt);
    const endIso = localDateTimeToISO(draft.endAt);
    if (!startIso || !endIso) return {error: t.submitErrNoDates};
    return {
        payload: {
            url: draft.url,
            name: draft.name.trim(),
            latitude: draft.latitude,
            longitude: draft.longitude,
            start_at: startIso,
            end_at: endIso,
        },
    };
};

const parsedDraft = (prev, url, data) => ({
    ...prev,
    url,
    name: data.title || url,
    title: data.title || '',
    description: data.description || '',
    imageUrl: data.image_url || null,
});

/** Parses the URL; ctx = {t, setDraft, setError, reportExisting}. */
const requestParse = async (url, ctx) => {
    try {
        const data = await (await postJson('/api/events/parse-url/', {url})).json();
        if (data.exists && data.event) return ctx.reportExisting(data.event, data);
        ctx.setDraft((prev) => parsedDraft(prev, url, data));
        return 'parsed';
    } catch (err) {
        ctx.setError(ctx.t.submitErrParseFailed);
        return 'failed';
    }
};

/** Sends the draft for moderation; ctx = {t, setDraft, setError, reportExisting}. */
const requestSubmit = async (payload, ctx) => {
    try {
        const response = await postJson('/api/events/submit/', payload);
        const data = await response.json();
        if (response.status === 409 && data.event) return ctx.reportExisting(data.event, data);
        if (response.ok && data.success) return 'sent';
        ctx.setError(data.error || ctx.t.submitErrSendFailed);
        return 'failed';
    } catch (err) {
        ctx.setError(ctx.t.submitErrNetwork);
        return 'failed';
    }
};

/** Shows the validation problem (resolves to 'invalid'), or clears the error and runs the request. */
const unlessInvalid = async (problem, setError, run) => {
    if (problem) {
        setError(problem);
        return 'invalid';
    }
    setError(null);
    return run();
};

/**
 * Returns {draft, setField(name, value), setPoint(lat, lng), parseUrl(input), submit(), reset(),
 * status ('idle' | 'parsing' | 'submitting'), error}.
 *
 * parseUrl resolves to 'invalid' (bad input, error set), 'existing' (an approved event already
 * has this URL; onExistingFound(event) was called), 'parsed' (draft filled) or 'failed'.
 * submit resolves to 'invalid', 'existing' (409 duplicate; onExistingFound called), 'sent' or
 * 'failed'. Errors use the camelCase submit labels (`submitErr*`).
 *
 * onExistingFound may return (a promise of) false when it could not show the event (v0.8:
 * the event modal did not open, e.g. a duplicate still in moderation): the result is then
 * 'duplicate', the draft stays and the error is the server's duplicate message (`data.error`,
 * else `labels.api_err_duplicate`). Any other return value (v0.7 returns nothing) is 'existing'.
 */
export const useSubmitEventDraft = ({labels, onExistingFound}) => {
    const t = labels || {};
    const [draft, setDraft] = useState(initialDraft);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const ctx = {
        t,
        setDraft,
        setError,
        reportExisting: async (event, data) => {
            const shown = onExistingFound ? await onExistingFound(event) : undefined;
            if (shown !== false) return 'existing';
            setError(data.error || t.api_err_duplicate);
            return 'duplicate';
        },
    };
    const whileBusy = async (busy, run) => {
        setStatus(busy);
        try {
            return await run();
        } finally {
            setStatus('idle');
        }
    };
    const parseUrl = (input) => {
        const url = (input || '').trim();
        return unlessInvalid(urlProblem(url, t), setError, () => whileBusy('parsing', () => requestParse(url, ctx)));
    };
    const submit = () => {
        const {error: problem, payload} = submitPayload(draft, t);
        return unlessInvalid(problem, setError, () => whileBusy('submitting', () => requestSubmit(payload, ctx)));
    };
    const reset = () => {
        setDraft(initialDraft());
        setStatus('idle');
        setError(null);
    };
    return {
        draft,
        setField: (name, value) => setDraft((prev) => ({...prev, [name]: value})),
        setPoint: (lat, lng) => setDraft((prev) => ({...prev, latitude: lat, longitude: lng})),
        parseUrl,
        submit,
        reset,
        status,
        error,
    };
};
